import React, { useEffect, useState } from "react";
import { DatePicker, Form, Input, InputNumber, Modal, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  CashEntry,
  CashHead,
  cashApiErrorMessage,
  projectCashApis,
} from "../../../api/ProjectCash";
import { cashHeadMeta } from "./projectCashShared";

/** "1234567.5" → "12,34,567.5" while typing (Indian grouping, no rounding). */
const groupIndian = (v?: string | number | null): string => {
  if (v === undefined || v === null || v === "") return "";
  const [intPart, dec] = String(v).split(".");
  const last3 = intPart.slice(-3);
  const rest = intPart.slice(0, -3);
  const grouped = rest
    ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${last3}`
    : last3;
  return dec !== undefined ? `${grouped}.${dec}` : grouped;
};

interface FormValues {
  entryMonth: Dayjs | null;
  referenceNo?: string;
  entryDate?: Dayjs | null;
  amount: string | null;
  remarks?: string;
}

// Add / edit one cash entry. NOTE the server's natural key is (project, head,
// month, referenceNo): creating an entry whose key already exists UPDATES that
// row — the server message ("Entry updated") is surfaced in the toast.
const CashEntryFormModal = ({
  open,
  onClose,
  onSaved,
  projectId,
  head,
  entry,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  projectId: number;
  head: CashHead;
  /** Present = edit, absent = create. */
  entry?: CashEntry | null;
}) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const meta = cashHeadMeta(head);
  const isEdit = !!entry?.cashEntryId;

  useEffect(() => {
    if (!open) return;
    if (entry) {
      form.setFieldsValue({
        entryMonth: entry.entryMonth ? dayjs(entry.entryMonth) : null,
        referenceNo: entry.referenceNo || "",
        entryDate: entry.entryDate ? dayjs(entry.entryDate) : null,
        amount: entry.amount ?? null,
        remarks: entry.remarks || "",
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ entryMonth: dayjs().startOf("month") });
    }
  }, [open, entry, form]);

  const handleOk = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const res = await projectCashApis.saveEntry({
        ...(isEdit ? { cashEntryId: entry!.cashEntryId } : {}),
        projectId,
        head,
        entryMonth: values.entryMonth!.format("YYYY-MM"),
        entryDate: values.entryDate ? values.entryDate.format("YYYY-MM-DD") : undefined,
        referenceNo: values.referenceNo?.trim() || undefined,
        amount: String(values.amount),
        remarks: values.remarks?.trim() || undefined,
      });
      if (res?.status) {
        message.success(res?.data?.message || res?.message || "Entry saved");
        onSaved();
        onClose();
      } else {
        message.error(cashApiErrorMessage(res, "Could not save the entry."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${isEdit ? "Edit" : "Add"} ${meta?.label || ""} entry`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEdit ? "Save changes" : "Add entry"}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="entryMonth"
          label="Month"
          rules={[{ required: true, message: "Pick the month" }]}
        >
          <DatePicker picker="month" format="MMM YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="referenceNo" label={meta?.referenceLabel || "Reference"}>
          <Input maxLength={100} placeholder={head === "RA_BILL" ? "e.g. RA-07" : "Optional"} />
        </Form.Item>
        <Form.Item name="entryDate" label="Date">
          <DatePicker format="DD MMM YYYY" style={{ width: "100%" }} placeholder="Optional" />
        </Form.Item>
        <Form.Item
          name="amount"
          label="Amount (₹)"
          rules={[
            { required: true, message: "Enter the amount" },
            {
              validator: (_, v) =>
                v === null || v === undefined || v === "" || Number(v) >= 0
                  ? Promise.resolve()
                  : Promise.reject(new Error("Amount cannot be negative")),
            },
          ]}
        >
          <InputNumber<string>
            stringMode
            min="0"
            precision={2}
            style={{ width: "100%" }}
            formatter={(v) => groupIndian(v)}
            parser={(v) => (v ? v.replace(/,/g, "") : "")}
          />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CashEntryFormModal;
